import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import ConfirmModal from '../shared/ConfirmModal';
import { getCourseScheduleEntries } from '../../utils/courseSchedule';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

// US Federal Holidays
const getNthWeekday = (year, month, weekday, n) => {
  const first = new Date(year, month, 1);
  const diff = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + diff + (n - 1) * 7);
};

const getLastWeekday = (year, month, weekday) => {
  const last = new Date(year, month + 1, 0);
  const diff = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - diff);
};

const getUSHolidays = (year) => [
  { date: new Date(year, 0, 1), name: "New Year's Day" },
  { date: getNthWeekday(year, 0, 1, 3), name: 'MLK Day' },
  { date: getNthWeekday(year, 1, 1, 3), name: "Presidents' Day" },
  { date: getLastWeekday(year, 4, 1), name: 'Memorial Day' },
  { date: new Date(year, 5, 19), name: 'Juneteenth' },
  { date: new Date(year, 6, 4), name: 'Independence Day' },
  { date: getNthWeekday(year, 8, 1, 1), name: 'Labor Day' },
  { date: getNthWeekday(year, 9, 1, 2), name: 'Columbus Day' },
  { date: new Date(year, 10, 11), name: "Veterans Day" },
  { date: getNthWeekday(year, 10, 4, 4), name: 'Thanksgiving' },
  { date: new Date(year, 11, 25), name: 'Christmas Day' },
];

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Two Dates land on the same UTC calendar day (matches the noon-UTC storage convention).
const sameNoonUtcDay = (a, b) => {
  if (!a || !b) return false;
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  return da.getUTCFullYear() === db.getUTCFullYear()
    && da.getUTCMonth() === db.getUTCMonth()
    && da.getUTCDate() === db.getUTCDate();
};

// Local-tz date matches a stored noon-UTC date (the same day on the wall calendar).
const sameLocalDayAsStored = (storedDate, localDate) => {
  if (!storedDate || !localDate) return false;
  const s = storedDate instanceof Date ? storedDate : new Date(storedDate);
  return s.getUTCFullYear() === localDate.getFullYear()
    && s.getUTCMonth() === localDate.getMonth()
    && s.getUTCDate() === localDate.getDate();
};

const CalendarPage = () => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [courses, setCourses] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ date: '', name: '', type: 'holiday' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [selectedDay, setSelectedDay] = useState(null);
  const [classModal, setClassModal] = useState({ open: false, event: null });
  const [notifyPrompt, setNotifyPrompt] = useState({ open: false, courseId: null, dateStr: '', courseName: '' });
  const [messageStep, setMessageStep] = useState({ open: false, courseId: null, dateStr: '', courseName: '', note: '', sending: false });
  const [confirmation, setConfirmation] = useState({ open: false, courseName: '', dateStr: '', notified: 0, hadNote: false });

  const fetchData = async () => {
    try {
      const [coursesRes, studioRes] = await Promise.all([
        api.get('/courses?status=active'),
        api.get('/studio')
      ]);
      setCourses(coursesRes.data.courses);
      setHolidays(studioRes.data.studio.holidays || []);
      setEvents(studioRes.data.studio.events || []);
    } catch (err) {
      console.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const prevMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  // Build events map for the visible calendar grid
  const buildEvents = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const result = {};

    const addEvent = (date, ev) => {
      const key = dateKey(date);
      if (!result[key]) result[key] = [];
      result[key].push(ev);
    };

    // US Holidays — current year (plus adjacent if month is Jan or Dec)
    const years = [year];
    if (month === 0) years.push(year - 1);
    if (month === 11) years.push(year + 1);
    years.forEach(y => {
      getUSHolidays(y).forEach(h =>
        addEvent(h.date, { type: 'us_holiday', name: h.name })
      );
    });

    // Studio holidays (noon UTC → render on the local-tz calendar day matching the UTC date)
    holidays.forEach(h => {
      const s = new Date(h.date);
      const local = new Date(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
      addEvent(local, { type: 'studio_holiday', name: h.name, id: h._id });
    });

    // Studio special events
    events.forEach(e => {
      const s = new Date(e.date);
      const local = new Date(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
      addEvent(local, { type: 'special_event', name: e.name, id: e._id });
    });

    // Course class sessions
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const gridStart = new Date(year, month, 1 - firstDay);
    const gridEnd = new Date(year, month, daysInMonth + (42 - daysInMonth - firstDay));

    courses.forEach(course => {
      const scheduleEntries = getCourseScheduleEntries(course);
      const entryByDayIdx = {};
      scheduleEntries.forEach(entry => {
        entryByDayIdx[DAY_MAP[entry.day]] = entry;
      });
      const cancelledDates = course.schedule?.cancelledDates || [];
      const isCancelledOn = (d) => cancelledDates.some(cd => sameLocalDayAsStored(cd, d));

      if (course.type === 'long_course' && scheduleEntries.length > 0) {
        const d = new Date(gridStart);
        while (d <= gridEnd) {
          const entry = entryByDayIdx[d.getDay()];
          if (entry) {
            addEvent(new Date(d), {
              type: 'class',
              name: course.name,
              time: entry.startTime || '',
              endTime: entry.endTime || '',
              courseId: course._id,
              cancelled: isCancelledOn(d)
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (course.type === 'block' && course.schedule?.blockStartDate) {
        const blockStart = new Date(course.schedule.blockStartDate);
        const blockWeeks = course.schedule.blockWeeks || 8;
        const blockEnd = new Date(blockStart);
        blockEnd.setDate(blockEnd.getDate() + blockWeeks * 7);

        const d = new Date(Math.max(blockStart.getTime(), gridStart.getTime()));
        const end = new Date(Math.min(blockEnd.getTime(), gridEnd.getTime()));
        while (d <= end) {
          const entry = entryByDayIdx[d.getDay()];
          if (entry) {
            addEvent(new Date(d), {
              type: 'block',
              name: course.name,
              time: entry.startTime || '',
              endTime: entry.endTime || '',
              courseId: course._id,
              cancelled: isCancelledOn(d)
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (course.type === 'workshop' && course.schedule?.sessionDate) {
        const d = new Date(course.schedule.sessionDate);
        addEvent(d, {
          type: 'workshop',
          name: course.name,
          time: course.schedule.sessionStartTime || '',
          endTime: course.schedule.sessionEndTime || '',
          courseId: course._id,
          cancelled: isCancelledOn(d)
        });
      }

      // Course assessments
      (course.assessments || []).forEach(assessment => {
        if (!assessment.date) return;
        const d = new Date(assessment.date);
        const isPast = d.getTime() < Date.now();
        addEvent(d, {
          type: 'assessment',
          name: assessment.title,
          time: assessment.startTime || '',
          isPast,
          courseId: course._id
        });
      });
    });

    return result;
  };

  const openAddEvent = (prefilledDate) => {
    setNewEvent({ date: prefilledDate || '', name: '', type: 'holiday' });
    setShowAddEvent(true);
  };

  const handleAddEvent = async () => {
    if (!newEvent.date || !newEvent.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/studio/events', newEvent);
      setHolidays(data.holidays || []);
      setEvents(data.events || []);
      setShowAddEvent(false);
      setNewEvent({ date: '', name: '', type: 'holiday' });
    } catch (err) {
      console.error('Failed to add event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      const { data } = await api.delete(`/studio/events/${id}`);
      setHolidays(data.holidays || []);
      setEvents(data.events || []);
      setDeleteConfirm({ open: false, id: null, name: '' });
    } catch (err) {
      console.error('Failed to delete entry');
    }
  };

  // Open the class details modal from a click on a class chip / day-row
  const openClassModal = (event, dayKey) => {
    setClassModal({ open: true, event: { ...event, dateStr: dayKey } });
  };

  const isClassCancelled = (courseId, dayKey) => {
    const c = courses.find(c => c._id === courseId);
    if (!c) return false;
    const target = new Date(dayKey + 'T12:00:00Z');
    return (c.schedule?.cancelledDates || []).some(cd => sameNoonUtcDay(cd, target));
  };

  const requestCancel = () => {
    if (!classModal.event) return;
    setNotifyPrompt({
      open: true,
      courseId: classModal.event.courseId,
      dateStr: classModal.event.dateStr,
      courseName: classModal.event.name
    });
    setClassModal({ open: false, event: null });
  };

  const handleRestore = async () => {
    if (!classModal.event) return;
    try {
      await api.delete(`/courses/${classModal.event.courseId}/cancellations/${classModal.event.dateStr}`);
      await fetchData();
      setClassModal({ open: false, event: null });
    } catch (err) {
      console.error('Failed to restore class');
    }
  };

  // "Cancel without notifying" — fire immediately from the prompt modal.
  const submitCancelSilent = async () => {
    try {
      await api.post(`/courses/${notifyPrompt.courseId}/cancellations`, {
        date: notifyPrompt.dateStr,
        notify: false
      });
      await fetchData();
      const courseName = notifyPrompt.courseName;
      const dateStr = notifyPrompt.dateStr;
      setNotifyPrompt({ open: false, courseId: null, dateStr: '', courseName: '' });
      setConfirmation({ open: true, courseName, dateStr, notified: 0, hadNote: false });
    } catch (err) {
      console.error('Failed to cancel class');
    }
  };

  // "Cancel and notify" — open the message step first.
  const openMessageStep = () => {
    setMessageStep({
      open: true,
      courseId: notifyPrompt.courseId,
      dateStr: notifyPrompt.dateStr,
      courseName: notifyPrompt.courseName,
      note: '',
      sending: false
    });
    setNotifyPrompt({ open: false, courseId: null, dateStr: '', courseName: '' });
  };

  const submitCancelWithMessage = async () => {
    setMessageStep(s => ({ ...s, sending: true }));
    try {
      const { data } = await api.post(`/courses/${messageStep.courseId}/cancellations`, {
        date: messageStep.dateStr,
        notify: true,
        note: messageStep.note.trim() || ''
      });
      await fetchData();
      const courseName = messageStep.courseName;
      const dateStr = messageStep.dateStr;
      const hadNote = !!messageStep.note.trim();
      setMessageStep({ open: false, courseId: null, dateStr: '', courseName: '', note: '', sending: false });
      setConfirmation({
        open: true,
        courseName,
        dateStr,
        notified: data?.notified || 0,
        hadNote
      });
    } catch (err) {
      console.error('Failed to cancel class', err);
      setMessageStep(s => ({ ...s, sending: false }));
    }
  };

  if (loading) return <div className="text-gray-500">Loading calendar...</div>;

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const eventsMap = buildEvents();
  const today = new Date();
  const todayKey = dateKey(today);

  // Build 42-cell grid
  const cells = [];
  const startDate = new Date(year, month, 1 - firstDay);
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    cells.push(d);
  }

  const lastCellMonth = cells[34].getMonth();
  const needsSixRows = lastCellMonth === month || cells[35].getDate() <= daysInMonth;
  const visibleCells = needsSixRows ? cells : cells.slice(0, 35);

  const selectedDayEvents = selectedDay ? (eventsMap[selectedDay] || []) : [];

  const chipClass = (event) => {
    let base = '';
    if (event.type === 'us_holiday') base = 'bg-red-50 text-red-600';
    else if (event.type === 'studio_holiday') base = 'bg-orange-50 text-orange-700 font-medium';
    else if (event.type === 'special_event') base = 'bg-brand-100 text-brand-700 font-medium';
    else if (event.type === 'workshop') base = 'bg-purple-50 text-purple-600';
    else if (event.type === 'block') base = 'bg-teal-50 text-teal-600';
    else if (event.type === 'assessment') {
      base = event.isPast ? 'bg-gray-100 text-gray-400 line-through' : 'bg-blue-50 text-blue-700 font-medium';
    } else base = 'bg-brand-50 text-brand-600';
    if (event.cancelled) base += ' line-through opacity-60';
    return base;
  };

  const dotClass = (event) => {
    if (event.type === 'us_holiday') return 'bg-red-500';
    if (event.type === 'studio_holiday') return 'bg-orange-500';
    if (event.type === 'special_event') return 'bg-brand-500';
    if (event.type === 'workshop') return 'bg-purple-500';
    if (event.type === 'block') return 'bg-teal-500';
    if (event.type === 'assessment') return event.isPast ? 'bg-gray-400' : 'bg-blue-500';
    return 'bg-brand-500';
  };

  const isClassEvent = (e) => e.type === 'class' || e.type === 'block' || e.type === 'workshop';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
        <button
          onClick={() => openAddEvent('')}
          className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors min-h-[44px]"
        >
          + Add Event
        </button>
      </div>

      {/* Calendar card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Month nav */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button
            onClick={prevMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 text-xl"
          >
            &#8249;
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {MONTH_NAMES[month]} {year}
            </h2>
            <button
              onClick={goToday}
              className="text-xs text-brand-500 hover:text-brand-600 font-medium px-2 py-1 rounded hover:bg-brand-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={nextMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 text-xl"
          >
            &#8250;
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
          {DAY_NAMES.map(d => (
            <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {visibleCells.map((date, i) => {
            const key = dateKey(date);
            const isCurrentMonth = date.getMonth() === month;
            const isToday = key === todayKey;
            const dayEvents = eventsMap[key] || [];
            const isSelected = selectedDay === key;

            return (
              <div
                key={i}
                onClick={() => setSelectedDay(isSelected ? null : key)}
                className={`min-h-[90px] border-b border-r border-gray-50 p-1.5 cursor-pointer transition-colors ${
                  !isCurrentMonth ? 'bg-gray-50/50' : ''
                } ${isToday ? 'bg-brand-50/30' : ''} ${
                  isSelected ? 'ring-2 ring-inset ring-brand-400' : ''
                } hover:bg-gray-50/80`}
              >
                <div className={`text-sm mb-1 ${
                  isToday
                    ? 'font-bold'
                    : !isCurrentMonth
                    ? 'text-gray-300'
                    : 'text-gray-700 font-medium'
                }`}>
                  {isToday ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold">
                      {date.getDate()}
                    </span>
                  ) : (
                    date.getDate()
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((event, j) => (
                    <div
                      key={j}
                      onClick={(e) => {
                        if (isClassEvent(event)) {
                          e.stopPropagation();
                          openClassModal(event, key);
                        }
                      }}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${chipClass(event)} ${isClassEvent(event) ? 'cursor-pointer hover:brightness-95' : ''}`}
                      title={event.name}
                    >
                      {event.name}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-brand-100 inline-block" /> Long Courses / Special Events
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-teal-100 inline-block" /> Short Programs
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-purple-100 inline-block" /> Workshops
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-100 inline-block" /> US Holidays
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-orange-100 inline-block" /> Studio Holidays
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-blue-100 inline-block" /> Assessments
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gray-100 inline-block line-through" /> Cancelled / Past
        </span>
      </div>

      {/* Day detail panel */}
      {selectedDay && (
        <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="text-sm font-semibold text-gray-700">
              {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
              })}
            </h3>
            <button
              onClick={() => openAddEvent(selectedDay)}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 px-3 py-2 rounded-lg border border-brand-200 hover:bg-brand-50 min-h-[36px]"
            >
              + Add Event
            </button>
          </div>
          {selectedDayEvents.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Nothing scheduled.</p>
          ) : (
            <div className="space-y-2">
              {selectedDayEvents.map((event, i) => (
                <div
                  key={i}
                  onClick={() => isClassEvent(event) && openClassModal(event, selectedDay)}
                  className={`flex items-center gap-3 ${isClassEvent(event) ? 'cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded' : ''}`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass(event)}`} />
                  <span className={`text-sm ${event.cancelled ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {event.name}
                  </span>
                  {event.cancelled && (
                    <span className="text-[10px] uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold">Cancelled</span>
                  )}
                  {event.time && (
                    <span className="text-xs text-gray-500">{event.time}</span>
                  )}
                  {(event.type === 'studio_holiday' || event.type === 'special_event') && event.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ open: true, id: event.id, name: event.name });
                      }}
                      className="text-xs text-red-500 hover:text-red-700 ml-auto"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Studio Holidays + Events lists */}
      {(holidays.length > 0 || events.length > 0) && (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {holidays.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Studio Holidays</h3>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
                {[...holidays]
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map(h => (
                    <div key={h._id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{h.name}</span>
                        <span className="text-sm text-gray-500 ml-3">
                          {(() => {
                            const d = new Date(h.date);
                            return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
                              .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          })()}
                        </span>
                      </div>
                      <button
                        onClick={() => setDeleteConfirm({ open: true, id: h._id, name: h.name })}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
          {events.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Special Events</h3>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
                {[...events]
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map(e => (
                    <div key={e._id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{e.name}</span>
                        <span className="text-sm text-gray-500 ml-3">
                          {(() => {
                            const d = new Date(e.date);
                            return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
                              .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          })()}
                        </span>
                      </div>
                      <button
                        onClick={() => setDeleteConfirm({ open: true, id: e._id, name: e.name })}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddEvent(false)} />
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Event</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newEvent.type}
                  onChange={e => setNewEvent(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent min-h-[44px]"
                >
                  <option value="holiday">Studio Holiday (closes the studio, suppresses reminders)</option>
                  <option value="special_event">Special Event (calendar marker, classes still happen)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newEvent.name}
                  onChange={e => setNewEvent(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={newEvent.type === 'holiday' ? 'Enter holiday name' : 'Enter event name'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent min-h-[44px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={e => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent min-h-[44px]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddEvent(false); setNewEvent({ date: '', name: '', type: 'holiday' }); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEvent}
                disabled={saving || !newEvent.date || !newEvent.name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:bg-gray-300 transition-colors min-h-[44px]"
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Class Details Modal */}
      {classModal.open && classModal.event && (() => {
        const ev = classModal.event;
        const cancelled = isClassCancelled(ev.courseId, ev.dateStr);
        const dateLabel = new Date(ev.dateStr + 'T12:00:00').toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
        });
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setClassModal({ open: false, event: null })} />
            <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{ev.name}</h3>
              <p className="text-sm text-gray-600 mb-1">{dateLabel}</p>
              {ev.time && (
                <p className="text-sm text-gray-500 mb-4">{ev.time}{ev.endTime ? ` – ${ev.endTime}` : ''}</p>
              )}
              {cancelled && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-sm font-semibold text-red-700">This class is cancelled.</p>
                  <p className="text-xs text-red-600 mt-1">No reminder will be sent. Students see this as cancelled in their dashboard.</p>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setClassModal({ open: false, event: null })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
                >
                  Close
                </button>
                {cancelled ? (
                  <button
                    onClick={handleRestore}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors min-h-[44px]"
                  >
                    Restore Class
                  </button>
                ) : (
                  <button
                    onClick={requestCancel}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors min-h-[44px]"
                  >
                    Cancel Class
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Notify Prompt — choose notify-or-not */}
      {notifyPrompt.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setNotifyPrompt({ open: false, courseId: null, dateStr: '', courseName: '' })} />
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Notify enrolled students?</h3>
            <p className="text-sm text-gray-600 mb-5">
              {notifyPrompt.courseName} on {new Date(notifyPrompt.dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} will be marked cancelled either way.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={submitCancelSilent}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
              >
                Cancel without notifying
              </button>
              <button
                onClick={openMessageStep}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors min-h-[44px]"
              >
                Cancel and notify students
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Optional message step (shown when teacher chose to notify) */}
      {messageStep.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !messageStep.sending && setMessageStep({ open: false, courseId: null, dateStr: '', courseName: '', note: '', sending: false })} />
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Send a message to students?</h3>
            <p className="text-sm text-gray-600 mb-4">
              {messageStep.courseName} on {new Date(messageStep.dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Optional message (e.g. reason, makeup info)</label>
            <textarea
              value={messageStep.note}
              onChange={e => setMessageStep(s => ({ ...s, note: e.target.value.slice(0, 1000) }))}
              rows={4}
              placeholder="Leave blank to send the standard cancellation email."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none"
            />
            <div className="text-xs text-gray-400 mt-1">{messageStep.note.length}/1000</div>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end mt-5">
              <button
                onClick={() => setMessageStep({ open: false, courseId: null, dateStr: '', courseName: '', note: '', sending: false })}
                disabled={messageStep.sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
              >
                Back
              </button>
              <button
                onClick={submitCancelWithMessage}
                disabled={messageStep.sending}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:bg-gray-300 transition-colors min-h-[44px]"
              >
                {messageStep.sending ? 'Sending...' : (messageStep.note.trim() ? 'Send & cancel' : 'Cancel & send standard email')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-send confirmation */}
      {confirmation.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmation({ open: false, courseName: '', dateStr: '', notified: 0, hadNote: false })} />
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Class cancelled</h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {confirmation.courseName}
                  {confirmation.dateStr && ` on ${new Date(confirmation.dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}`}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 ml-13">
              {confirmation.notified > 0
                ? `Email${confirmation.hadNote ? ' (with your message)' : ''} sent to ${confirmation.notified} student${confirmation.notified === 1 ? '' : 's'}.`
                : 'No notification sent.'}
            </p>
            <div className="flex justify-end mt-5">
              <button
                onClick={() => setConfirmation({ open: false, courseName: '', dateStr: '', notified: 0, hadNote: false })}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors min-h-[44px]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Entry"
        message={`Remove "${deleteConfirm.name}" from your calendar?`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => handleDeleteEvent(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
      />
    </div>
  );
};

export default CalendarPage;
